import { useEffect, useState } from "react";

interface DeviceInfo {
  isMobile: boolean;
  hasWalletExtension: boolean;
  isMobileWithoutExtension: boolean;
  userAgent: string;
  walletType: "metamask" | "okx" | "coinbase" | "generic" | "none";
  isOKXBrowser: boolean;
}

export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isMobile: false,
    hasWalletExtension: false,
    isMobileWithoutExtension: false,
    userAgent: "",
    walletType: "none",
    isOKXBrowser: false,
  });

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent;
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );

      // Check for OKX wallet browser specifically
      const isOKXBrowser =
        !!(window as any).okxwallet || userAgent.includes("OKApp");

      // Enhanced wallet detection
      const hasOKXWallet = !!(window as any).okxwallet;
      const hasMetaMask = !!(window as any).ethereum?.isMetaMask;
      const hasCoinbaseWallet =
        !!(window as any).ethereum?.isCoinbaseWallet ||
        !!(window as any).coinbaseWalletExtension;
      const hasGenericEthereum = !!(window as any).ethereum;

      const hasWalletExtension =
        hasOKXWallet || hasMetaMask || hasCoinbaseWallet || hasGenericEthereum;
      const isMobileWithoutExtension = isMobile && !hasWalletExtension;

      // Determine wallet type priority: OKX > MetaMask > Coinbase > Generic > None
      // Special handling: if multiple wallets detected, prefer non-Phantom for Ethereum
      let walletType: "metamask" | "okx" | "coinbase" | "generic" | "none" =
        "none";

      const multipleWallets =
        [
          hasOKXWallet,
          hasMetaMask,
          hasCoinbaseWallet,
          hasGenericEthereum,
        ].filter(Boolean).length > 1;

      if (hasOKXWallet) {
        walletType = "okx";
      } else if (
        hasMetaMask &&
        !((window as any).ethereum?.isPhantom && multipleWallets)
      ) {
        walletType = "metamask";
      } else if (hasCoinbaseWallet) {
        walletType = "coinbase";
      } else if (hasGenericEthereum) {
        // If Phantom is present with other wallets, mark as generic to show conflict warning
        walletType = "generic";
      }

      setDeviceInfo({
        isMobile,
        hasWalletExtension,
        isMobileWithoutExtension,
        userAgent,
        walletType,
        isOKXBrowser,
      });
    };

    checkDevice();

    // Re-check when ethereum providers change
    const handleEthereumChange = () => {
      checkDevice();
    };

    // Listen for wallet changes
    if ((window as any).ethereum) {
      (window as any).ethereum.on("connect", handleEthereumChange);
      (window as any).ethereum.on("disconnect", handleEthereumChange);
    }

    return () => {
      if ((window as any).ethereum) {
        (window as any).ethereum.removeListener(
          "connect",
          handleEthereumChange
        );
        (window as any).ethereum.removeListener(
          "disconnect",
          handleEthereumChange
        );
      }
    };
  }, []);

  return deviceInfo;
};
